/**
 * マッピング設定 Excel ダウンロード: マッピング内容（K 列）の整形。
 *
 * 実装計画 6.4 に対応する。判定ルール設定画面と同じ IF 記法で出力し、 利用者が見慣れた表記で内容を把握できるようにする。
 *
 * - 参照元カラム: `[データ分類.項目名]`
 * - 条件: `IF <条件> → <値>` / 最終行は `ELSE → <値>`
 * - 空値: `(空)`
 * - 比較演算子は記号のまま（`==` / `>=` 等）
 *
 * GenericMapping 画面の `formatMappingItemLabel` は一覧表示用の短いラベル （例「日付形式設定」）しか返さないため、Excel 用に本モジュールを別途用意する。
 */
import jsep from 'jsep'

/** 空値の表示文字列 */
export const EMPTY_VALUE_LABEL = '(空)'

/** 判定ルールが解決できなかった場合の既定文言 */
const JUDGMENT_RULE_FALLBACK = '判定ルール適用'

/** ELSE を表す条件式 AST（jsep の Literal true） */
const ELSE_CONDITION_LITERAL = 'true'

/** 条件が複数行になる場合、2 行目以降のインデント */
const CONDITION_INDENT = '   '

/** マッピングタイプ → 画面表示ラベル（GenericMapping/extra/constants.js の文言に準拠） */
const MAPPING_METHOD_LABELS = {
  simple: 'そのまま取り込む',
  fixed: '固定値を設定する',
  conversion_table: 'マスターで変換する',
  value_conversion: '個別に値を変換する',
  value_conversion_manual: '個別に値を変換する',
  date_conversion: '日付形式を変換する',
  time_conversion: '時間形式を変換する',
  arithmetic: '計算して求める（四則演算）',
  condition: '条件によって値を変える',
  condition_multiple: '複数の条件を組み合わせる',
  judgment_rule: '判定ルール',
  // remove はステータス列に「不要」を出すため、方法列は空にする
  remove: ''
}

/**
 * データ型 → 日本語ラベル。
 *
 * CHROFY のデータ型は `string` / `int` / `decimal` / `date` の 4 種 （汎用CHROFY項目.md）。表記は同設計書の ErrorType 一覧に合わせる。
 */
const DATA_TYPE_LABELS = {
  string: '文字列',
  int: '整数',
  decimal: '数値',
  date: '日付'
}

/** Num_agg_type → 表示ラベル */
const NUM_AGG_TYPE_LABELS = {
  priority: '優先順位',
  total: '合算'
}

// --- 汎用ユーティリティ -------------------------------------------------

/**
 * オブジェクトから最初に見つかったキーの値を返す（snake_case / camelCase / PascalCase 混在に対応）
 *
 * @param {Record<string, unknown> | null | undefined} obj
 * @param {...string} keys
 * @returns {unknown}
 */
function pick(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined
  for (const key of keys) {
    const value = /** @type {Record<string, unknown>} */ (obj)[key]
    if (value !== undefined && value !== null) return value
  }
  return undefined
}

/**
 * 値をレコードとして扱う（オブジェクト以外は空オブジェクト）
 *
 * @param {unknown} value
 * @returns {Record<string, unknown>}
 */
function asRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? /** @type {Record<string, unknown>} */ (value)
    : {}
}

/**
 * 値を配列として扱う
 *
 * @param {unknown} value
 * @returns {unknown[]}
 */
function asArray(value) {
  return Array.isArray(value) ? value : []
}

/**
 * 空文字・null・undefined を `(空)` に置き換えた表示文字列を返す
 *
 * @param {unknown} value
 * @returns {string}
 */
function toDisplayValue(value) {
  const str = value === null || value === undefined ? '' : String(value)
  return str === '' ? EMPTY_VALUE_LABEL : str
}

// --- AST → IF 記法 -----------------------------------------------------

/**
 * `col(分類, 項目)` の CallExpression から `[分類.項目]` を組み立てる。 判定ルールの `RANK(col(...))` にも対応する。
 *
 * @param {Record<string, unknown>} node
 * @returns {string | null}
 */
function callExpressionToText(node) {
  const callee = asRecord(pick(node, 'callee', 'Callee'))
  const name = String(pick(callee, 'name', 'Name') ?? pick(node, 'name', 'Name') ?? '')
  const args = asArray(pick(node, 'arguments', 'Arguments'))

  if (name === 'col') {
    const parts = args.map((arg) => String(pick(asRecord(arg), 'value', 'Value') ?? ''))
    // col("RANK([分類.項目])") 形式（判定ルール用の RANK 列参照）はそのまま返す
    if (parts.length === 1) return parts[0]
    const [fileType, column] = parts
    return `[${fileType}.${column}]`
  }

  if (name === '') return null

  const inner = args.map((arg) => astToText(arg)).join(', ')
  return `${name}(${inner})`
}

/**
 * 演算子の優先順位。数値が大きいほど強く結合する。
 *
 * 括弧の有無で計算結果が変わるため、AST → 文字列化では 親子の優先順位を比較して必要な括弧だけを復元する。
 *
 * @param {string} operator 正規化済みの演算子
 * @returns {number}
 */
function operatorPrecedence(operator) {
  switch (operator) {
    case 'OR':
      return 1
    case 'AND':
      return 2
    case '==':
    case '!=':
    case '>':
    case '>=':
    case '<':
    case '<=':
      return 3
    case '+':
    case '-':
      return 4
    case '*':
    case '/':
    case '%':
      return 5
    default:
      // 未知の演算子は最も弱いものとして扱い、安全側（括弧あり）に倒す
      return 0
  }
}

/**
 * 条件式 / 演算式の AST を IF 記法のテキストへ変換する
 *
 * @param {unknown} node
 * @param {number} [parentPrecedence=0] 親ノードの演算子優先順位。 子の優先順位がこれより低い場合のみ括弧で囲む。. Default is `0`
 * @returns {string}
 */
function astToText(node, parentPrecedence = 0) {
  if (node === null || node === undefined) return ''
  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
    return String(node)
  }

  const record = asRecord(node)
  const type = String(pick(record, 'type', 'Type') ?? '')

  switch (type) {
    case 'Literal': {
      const value = pick(record, 'value', 'Value')
      if (typeof value === 'string') {
        // 画面で「(空)」を選んだ場合は空文字リテラルとして保存される。
        // 比較値・結果値のどちらでも同じ表記になるよう、ここで (空) にする。
        return value === '' ? EMPTY_VALUE_LABEL : `"${value}"`
      }
      return String(value ?? '')
    }
    case 'Identifier':
      return String(pick(record, 'name', 'Name') ?? '')
    case 'CallExpression':
      return callExpressionToText(record) ?? ''
    case 'UnaryExpression':
      return `${String(pick(record, 'operator', 'Operator') ?? '')}${astToText(pick(record, 'argument', 'Argument'))}`
    case 'BinaryExpression':
    case 'LogicalExpression': {
      const operator = String(pick(record, 'operator', 'Operator') ?? '')
      const normalizedOperator = normalizeOperator(operator)
      const precedence = operatorPrecedence(normalizedOperator)
      // 左結合のため、右辺は同順位でも括弧が必要（a - (b - c) ≠ a - b - c）
      const left = astToText(pick(record, 'left', 'Left'), precedence)
      const right = astToText(pick(record, 'right', 'Right'), precedence + 1)
      const text = `${left} ${normalizedOperator} ${right}`
      // 親より優先順位が低い場合のみ括弧で囲む（括弧の有無で計算結果が変わるため）
      return precedence < parentPrecedence ? `(${text})` : text
    }
    // 旧データ互換: { Type: 'Column', Arguments: [...] }
    case 'Column':
      return callExpressionToText({ ...record, name: 'col' }) ?? ''
    default:
      return ''
  }
}

/**
 * 論理演算子を IF 記法（AND / OR）へ正規化する。比較演算子は記号のまま返す。
 *
 * @param {string} operator
 * @returns {string}
 */
function normalizeOperator(operator) {
  if (operator === '&&' || operator === 'AND' || operator === 'and') return 'AND'
  if (operator === '||' || operator === 'OR' || operator === 'or') return 'OR'
  return operator
}

/**
 * 保存済みの条件式（AST の JSON 文字列、または式文字列）をテキストへ変換する。
 *
 * パースできない場合は入力文字列をそのまま返し、Excel 出力が失敗しないようにする。
 *
 * @param {unknown} stored
 * @returns {string}
 */
function storedExpressionToText(stored) {
  if (stored === null || stored === undefined) return ''
  if (typeof stored === 'object') return astToText(stored)

  const raw = String(stored).trim()
  if (raw === '') return ''

  // AST の JSON 文字列
  if (raw.startsWith('{') || raw.startsWith('[')) {
    try {
      return astToText(JSON.parse(raw))
    } catch {
      return raw
    }
  }

  // 式文字列（col("分類", "項目") 形式）
  try {
    return astToText(jsep(raw))
  } catch {
    return raw
  }
}

/**
 * 保存済みの「値」（AST の JSON 文字列）を表示用テキストへ変換する。 空値は `(空)` にする。
 *
 * @param {unknown} stored
 * @returns {string}
 */
function storedValueToText(stored) {
  const text = storedExpressionToText(stored)
  // 空文字リテラルは astToText 側で (空) に変換済み。
  // ここでは値そのものが未保存（空）の場合をカバーする。
  if (text === '') return EMPTY_VALUE_LABEL
  return text
}

// --- 公開ラベル関数 -----------------------------------------------------

/**
 * マッピングタイプを日本語ラベルへ変換する（Excel F 列）
 *
 * @param {unknown} mappingType
 * @returns {string}
 */
export function formatMappingMethodLabel(mappingType) {
  if (mappingType === null || mappingType === undefined) return ''
  const key = String(mappingType)
  if (key === '') return ''
  return MAPPING_METHOD_LABELS[key] ?? key
}

/**
 * 数値集計タイプを日本語ラベルへ変換する（Excel H 列）
 *
 * @param {unknown} numAggType
 * @returns {string}
 */
export function formatNumAggTypeLabel(numAggType) {
  if (numAggType === null || numAggType === undefined) return ''
  return NUM_AGG_TYPE_LABELS[String(numAggType)] ?? ''
}

/**
 * データ型を日本語ラベルへ変換する（Excel D 列）。 未知の値はそのまま返し、情報を失わせない。
 *
 * @param {unknown} dataType
 * @returns {string}
 */
export function formatDataTypeLabel(dataType) {
  if (dataType === null || dataType === undefined) return ''
  const key = String(dataType).trim()
  if (key === '') return ''
  return DATA_TYPE_LABELS[key.toLowerCase()] ?? key
}

// --- マッピングタイプ別の整形 -------------------------------------------

/**
 * ソースカラム情報から `分類／項目` の表示文字列を組み立てる
 *
 * @param {Record<string, unknown>} source
 * @returns {{ fileType: string; columnName: string }}
 */
function readSourceColumn(source) {
  return {
    fileType: String(pick(source, 'mapping_file_type', 'mappingFileType', 'MappingFileType') ?? ''),
    columnName: String(pick(source, 'mapping_column_name', 'mappingColumnName', 'MappingColumnName') ?? '')
  }
}

/**
 * Simple: そのまま取り込む
 *
 * @param {Record<string, unknown>} info
 * @returns {string}
 */
function formatSimple(info) {
  const source = asRecord(pick(info, 'simple_mapping', 'simpleMapping', 'SimpleMapping'))
  const { fileType, columnName } = readSourceColumn(source)
  if (fileType === '' && columnName === '') return ''
  // ファイル種別は【】、項目名は「」で囲み、両者を見分けやすくする
  return `【${fileType}】の「${columnName}」をそのまま`
}

/**
 * Fixed: 固定値
 *
 * @param {Record<string, unknown>} info
 * @returns {string}
 */
function formatFixed(info) {
  const constant = pick(info, 'constant', 'Constant')
  if (constant === undefined && !('constant' in info) && !('Constant' in info)) return ''
  return `固定値: ${toDisplayValue(constant ?? info.constant ?? info.Constant)}`
}

/**
 * Conversion_table: マスターで変換する
 *
 * @param {Record<string, unknown>} info
 * @param {{ conversionTableList?: { conversionTableKey?: string; conversionTableName?: string }[] }} options
 * @returns {string}
 */
function formatConversionTable(info, options) {
  const source = asRecord(pick(info, 'conversion_table', 'conversionTable', 'ConversionTable'))
  const { fileType, columnName } = readSourceColumn(source)
  const tableKey = String(pick(source, 'conversion_table_key', 'conversionTableKey', 'ConversionTableKey') ?? '')
  if (tableKey === '' && fileType === '' && columnName === '') return ''

  const list = options?.conversionTableList ?? []
  const found = list.find((row) => String(row?.conversionTableKey ?? row?.conversion_table_key ?? '') === tableKey)
  const tableName = String(found?.conversionTableName ?? found?.conversion_table_name ?? tableKey)

  const sourcePart = fileType !== '' || columnName !== '' ? `（元: ${fileType}／${columnName}）` : ''
  return `変換表「${tableName}」で変換${sourcePart}`
}

/**
 * Value_conversion / value_conversion_manual: 個別に値を変換する
 *
 * @param {Record<string, unknown>} info
 * @returns {string}
 */
function formatValueConversion(info) {
  const source = asRecord(pick(info, 'value_mapping', 'valueMapping', 'ValueMapping'))
  const list = asArray(pick(source, 'mapping_list', 'mappingList', 'MappingList'))
  if (list.length === 0) return ''

  return list
    .map((row) => {
      const record = asRecord(row)
      const from = toDisplayValue(pick(record, 'from', 'from_value', 'fromValue') ?? '')
      const to = toDisplayValue(pick(record, 'to', 'to_value', 'toValue') ?? '')
      return `${from} → ${to}`
    })
    .join(' ／ ')
}

/**
 * Date_conversion / time_conversion: 形式変換
 *
 * @param {Record<string, unknown>} info
 * @param {'date' | 'time'} kind
 * @returns {string}
 */
function formatFormatConversion(info, kind) {
  const source =
    kind === 'date'
      ? asRecord(pick(info, 'source_date_format', 'sourceDateFormat', 'SourceDateFormat'))
      : asRecord(pick(info, 'source_time_format', 'sourceTimeFormat', 'SourceTimeFormat'))
  const format = String(pick(source, 'format', 'Format') ?? '')
  if (format === '') return ''
  const target = kind === 'date' ? '標準日付' : '標準時間'
  return `${format} → ${target}`
}

/**
 * Arithmetic: 四則演算
 *
 * @param {Record<string, unknown>} info
 * @returns {string}
 */
function formatArithmetic(info) {
  const stored = pick(info, 'four_arithmetic_expressions', 'fourArithmeticExpressions', 'FourArithmeticExpressions')
  const text = storedExpressionToText(stored)
  // 式全体が括弧で囲まれる場合も、そのままだと冗長にならないので許容する
  return text
}

/**
 * Condition: 条件によって値を変える（単一条件）
 *
 * @param {Record<string, unknown>} info
 * @returns {string}
 */
function formatCondition(info) {
  const rows = asArray(pick(info, 'single_condition', 'singleCondition', 'SingleCondition'))
  if (rows.length === 0) return ''

  const lines = rows.map((row) => {
    const record = asRecord(row)
    const rawCondition = pick(record, 'conditional_expression', 'conditionalExpression', 'ConditionalExpression')
    const value = storedValueToText(pick(record, 'value', 'Value'))

    if (isElseCondition(rawCondition)) return `ELSE → ${value}`
    const condition = storedExpressionToText(rawCondition)
    return `IF ${condition} → ${value}`
  })

  return lines.join('\n')
}

/**
 * ELSE 行かどうか（条件式が空、または Literal true）
 *
 * @param {unknown} rawCondition
 * @returns {boolean}
 */
function isElseCondition(rawCondition) {
  if (rawCondition === null || rawCondition === undefined) return true
  if (typeof rawCondition === 'object') {
    const record = asRecord(rawCondition)
    return String(pick(record, 'type', 'Type') ?? '') === 'Literal' && pick(record, 'value', 'Value') === true
  }
  const raw = String(rawCondition).trim()
  if (raw === '' || raw === ELSE_CONDITION_LITERAL) return true
  if (raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw)
      return parsed?.type === 'Literal' && parsed?.value === true
    } catch {
      return false
    }
  }
  return false
}

/**
 * 条件テキストが「常に真」（ELSE 相当）かどうか。
 *
 * ELSE 行は保存形式が揺れており、条件式が空・`true`・Literal true の AST いずれの 場合もある。`IF true` と出力されるのを防ぐために判定する。
 *
 * @param {string} text
 * @returns {boolean}
 */
function isAlwaysTrueText(text) {
  const normalized = String(text ?? '')
    .trim()
    .replace(/^\((.*)\)$/, '$1')
    .trim()
    .toLowerCase()
  return normalized === 'true'
}

/**
 * Condition_multiple: 複数の条件を組み合わせる
 *
 * 1 つの条件セット内の式を `join_condition` で連結し、 セット同士も直列に連結する。2 行目以降はインデントする。
 *
 * @param {Record<string, unknown>} info
 * @returns {string}
 */
function formatConditionMultiple(info) {
  const rows = asArray(pick(info, 'multi_condition', 'multiCondition', 'MultiCondition'))
  if (rows.length === 0) return ''

  const lines = []
  for (const row of rows) {
    const record = asRecord(row)
    const value = storedValueToText(pick(record, 'value', 'Value'))
    const sets = asArray(pick(record, 'condition_set', 'conditionSet', 'ConditionSet'))

    if (sets.length === 0) {
      lines.push(`ELSE → ${value}`)
      continue
    }

    const setTexts = sets.map((set) => conditionSetToText(asRecord(set)))
    const [firstSet, ...restSets] = setTexts.filter((text) => text.text !== '')
    if (!firstSet) {
      lines.push(`ELSE → ${value}`)
      continue
    }
    // ELSE 行は条件セットに Literal true が入る形でも保存される（複数セットに
    // 分かれている場合もある）。そのまま出すと「IF true」になり単一条件の
    // ELSE 表記と揺れるため、全セットが常に真なら ELSE に揃える。
    if (setTexts.every((set) => set.text === '' || isAlwaysTrueText(set.text))) {
      lines.push(`ELSE → ${value}`)
      continue
    }

    lines.push(`IF ${firstSet.text}`)
    restSets.forEach((set, index) => {
      const isLast = index === restSets.length - 1
      const suffix = isLast ? ` → ${value}` : ''
      lines.push(`${CONDITION_INDENT}${set.join} ${set.text}${suffix}`)
    })
    if (restSets.length === 0) {
      // 単一セットのみの場合は 1 行目に値を付ける
      lines[lines.length - 1] = `IF ${firstSet.text} → ${value}`
    }
  }

  return lines.join('\n')
}

/**
 * 条件セット 1 件をテキストへ変換する。 式が 2 件以上ある場合は括弧で囲む。
 *
 * @param {Record<string, unknown>} set
 * @returns {{ join: string; text: string }}
 */
function conditionSetToText(set) {
  const join = normalizeOperator(String(pick(set, 'join_condition', 'joinCondition', 'JoinCondition') ?? 'and'))
  const expressions = asArray(
    pick(set, 'conditional_expression_list', 'conditionalExpressionList', 'ConditionalExpressionList')
  )
  const texts = expressions
    .map((expr) =>
      storedExpressionToText(
        pick(asRecord(expr), 'conditional_expression', 'conditionalExpression', 'ConditionalExpression')
      )
    )
    .filter((text) => text !== '')

  if (texts.length === 0) return { join, text: '' }
  if (texts.length === 1) return { join, text: texts[0] }
  return { join, text: `(${texts.join(` ${join} `)})` }
}

// --- 判定ルール ---------------------------------------------------------

/**
 * 判定ルール（IF02092 の 1 レコード）を IF 形式のテキストへ変換する。
 *
 * IF02092 は `revert_condition_format` により `condition` を すでに `[分類.項目]` 形式で返すため、AST の再解析は不要。 `judgment_rules` の最終要素は
 * ELSE（`condition` が空）。
 *
 * @param {Record<string, unknown> | null | undefined} rule
 * @returns {string}
 */
export function formatJudgmentRuleContent(rule) {
  if (!rule || typeof rule !== 'object') return JUDGMENT_RULE_FALLBACK

  const ruleName = String(pick(rule, 'ruleName', 'rule_name', 'RuleName') ?? '')
  const rows = asArray(pick(rule, 'judgmentRules', 'judgment_rules', 'JudgmentRules'))

  const lines = []
  if (ruleName !== '') lines.push(`ルール名：${ruleName}`)

  for (const row of rows) {
    const record = asRecord(row)
    const condition = String(pick(record, 'condition', 'Condition') ?? '').trim()
    const value = quoteJudgmentValue(pick(record, 'value', 'Value'))
    if (condition === '') {
      lines.push(`ELSE → ${value}`)
    } else {
      lines.push(`IF ${normalizeJudgmentCondition(condition)} → ${value}`)
    }
  }

  if (lines.length === 0) return JUDGMENT_RULE_FALLBACK
  return lines.join('\n')
}

/**
 * 判定ルールの条件式内の空文字リテラル（`""`）を `(空)` に置き換える。 それ以外は IF02092 の表記をそのまま用いる。
 *
 * @param {string} condition
 * @returns {string}
 */
function normalizeJudgmentCondition(condition) {
  return condition.replace(/""/g, EMPTY_VALUE_LABEL)
}

/**
 * 判定ルールの判定結果（`value`）を表示用に整える。
 *
 * `JudgmentRule.Value` は AST ではなく素の文字列で保持されるため、 条件（`condition`）側の表記と揃えるためにダブルクォートで囲む。 空値は `(空)` とし、クォートしない。
 *
 * @param {unknown} value
 * @returns {string}
 */
function quoteJudgmentValue(value) {
  const raw = value === null || value === undefined ? '' : String(value)
  if (raw === '') return EMPTY_VALUE_LABEL
  return `"${raw}"`
}

// --- エントリポイント ---------------------------------------------------

/** マッピングタイプ別の整形ハンドラ */
const CONTENT_FORMATTERS = {
  simple: (info) => formatSimple(info),
  fixed: (info) => formatFixed(info),
  conversion_table: (info, options) => formatConversionTable(info, options),
  value_conversion: (info) => formatValueConversion(info),
  value_conversion_manual: (info) => formatValueConversion(info),
  date_conversion: (info) => formatFormatConversion(info, 'date'),
  time_conversion: (info) => formatFormatConversion(info, 'time'),
  arithmetic: (info) => formatArithmetic(info),
  condition: (info) => formatCondition(info),
  condition_multiple: (info) => formatConditionMultiple(info),
  judgment_rule: (_info, options) => formatJudgmentRuleContent(options?.judgmentRule),
  // remove はステータス列で「不要」と表示するため内容は空にする
  remove: () => ''
}

/**
 * マッピング内容（Excel K 列）を整形する。
 *
 * @param {unknown} mappingType - IF02085 の mapping_type
 * @param {unknown} mappingInfo - IF02085 の mapping_info
 * @param {{
 *   conversionTableList?: { conversionTableKey?: string; conversionTableName?: string }[]
 *   judgmentRule?: Record<string, unknown> | null
 * }} [options]
 * @returns {string}
 */
export function formatMappingContent(mappingType, mappingInfo, options = {}) {
  const key = mappingType === null || mappingType === undefined ? '' : String(mappingType)
  const formatter = CONTENT_FORMATTERS[key]
  if (!formatter) return ''
  return formatter(asRecord(mappingInfo), options)
}
